import type { Edge, Node } from '../../../core/models/scene';
import type {
  AllocationLoadStatus,
  OrgAllocationTargetRoster,
  OrgAllocationView,
  OrgPersonAllocationLoad,
} from './OrgAllocationService';

export interface OrgAllocationDiffSceneOptions {
  readonly origin?: { readonly x: number; readonly y: number };
  readonly idPrefix?: string;
  readonly maxIncreases?: number;
  readonly maxDecreases?: number;
  readonly maxTargets?: number;
}

export interface OrgAllocationDiffScene {
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly changedPeopleCount: number;
  readonly changedTargetCount: number;
  readonly hiddenIncreaseCount: number;
  readonly hiddenDecreaseCount: number;
  readonly hiddenTargetCount: number;
}

interface PersonDiff {
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly beforeLoad: number;
  readonly afterLoad: number;
  readonly delta: number;
  readonly beforeStatus: AllocationLoadStatus | null;
  readonly afterStatus: AllocationLoadStatus | null;
  readonly beforeAssignments: number;
  readonly afterAssignments: number;
  readonly hasUnknownBefore: boolean;
  readonly hasUnknownAfter: boolean;
  readonly workSummary: string;
}

interface TargetDiff {
  readonly targetExternalId: string;
  readonly targetLabel: string;
  readonly targetKind: string;
  readonly beforeLoad: number;
  readonly afterLoad: number;
  readonly delta: number;
  readonly beforePeople: number;
  readonly afterPeople: number;
  readonly hasUnknownBefore: boolean;
  readonly hasUnknownAfter: boolean;
}

const DEFAULT_ORIGIN = { x: 120, y: 120 } as const;
const GRID = 12;
const KPI_WIDTH = 220;
const KPI_HEIGHT = 92;
const KPI_GAP = 20;
const LANE_TOP = 144;
const LANE_PADDING = 20;
const LANE_HEADER_RESERVE = 72;
const PERSON_CARD_WIDTH = 300;
const PERSON_CARD_HEIGHT = 180;
const PERSON_CARD_GAP = 18;
const TARGET_CARD_WIDTH = 320;
const TARGET_CARD_HEIGHT = 96;
const TARGET_CARD_GAP = 16;
const PEOPLE_LANE_WIDTH = PERSON_CARD_WIDTH + LANE_PADDING * 2;
const TARGET_LANE_WIDTH = TARGET_CARD_WIDTH + LANE_PADDING * 2;
const LANE_GAP = 28;
const DEFAULT_PERSON_LIMIT = 4;
const DEFAULT_TARGET_LIMIT = 7;
const EPSILON = 0.0001;

export function buildOrgAllocationDiffScene(
  before: OrgAllocationView,
  after: OrgAllocationView,
  options: OrgAllocationDiffSceneOptions = {},
): OrgAllocationDiffScene {
  const origin = snapPoint(options.origin ?? DEFAULT_ORIGIN);
  const idPrefix = options.idPrefix ?? `atlas-diff-${Date.now().toString(36)}`;
  const increaseLimit = boundedLimit(options.maxIncreases, DEFAULT_PERSON_LIMIT);
  const decreaseLimit = boundedLimit(options.maxDecreases, DEFAULT_PERSON_LIMIT);
  const targetLimit = boundedLimit(options.maxTargets, DEFAULT_TARGET_LIMIT);

  const personDiffs = buildPersonDiffs(before.people, after.people);
  const targetDiffs = buildTargetDiffs(before.targets, after.targets);
  const increases = personDiffs
    .filter((diff) => diff.delta > EPSILON)
    .sort((a, b) => b.delta - a.delta || a.personLabel.localeCompare(b.personLabel));
  const decreases = personDiffs
    .filter((diff) => diff.delta < -EPSILON)
    .sort((a, b) => a.delta - b.delta || a.personLabel.localeCompare(b.personLabel));
  const changedTargets = targetDiffs
    .filter((diff) => Math.abs(diff.delta) > EPSILON ||
      diff.beforePeople !== diff.afterPeople ||
      diff.hasUnknownBefore !== diff.hasUnknownAfter)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) ||
      a.targetLabel.localeCompare(b.targetLabel));

  const visibleIncreases = increases.slice(0, increaseLimit);
  const visibleDecreases = decreases.slice(0, decreaseLimit);
  const visibleTargets = changedTargets.slice(0, targetLimit);
  const increaseHeight = laneHeight(visibleIncreases.length, PERSON_CARD_HEIGHT, PERSON_CARD_GAP);
  const decreaseHeight = laneHeight(visibleDecreases.length, PERSON_CARD_HEIGHT, PERSON_CARD_GAP);
  const targetHeight = laneHeight(visibleTargets.length, TARGET_CARD_HEIGHT, TARGET_CARD_GAP);
  const laneHeightPx = Math.max(increaseHeight, decreaseHeight, targetHeight, 420);

  const increaseX = origin.x;
  const decreaseX = increaseX + PEOPLE_LANE_WIDTH + LANE_GAP;
  const targetX = decreaseX + PEOPLE_LANE_WIDTH + LANE_GAP;
  const lanesY = origin.y + LANE_TOP;
  const addedLoad = sumPositiveDelta(increases);
  const freedLoad = Math.abs(sumNegativeDelta(decreases));

  const nodes: Node[] = [
    makeKpiCard(idPrefix, 'people', origin.x, origin.y, {
      title: String(personDiffs.length),
      subtitle: 'people with changed load',
      accent: '#2563EB',
      fill: '#EFF6FF',
      stroke: '#BFDBFE',
    }),
    makeKpiCard(idPrefix, 'added', origin.x + (KPI_WIDTH + KPI_GAP), origin.y, {
      title: formatSignedAllocation(addedLoad),
      subtitle: 'added load',
      accent: '#EA580C',
      fill: '#FFF7ED',
      stroke: '#FDBA74',
    }),
    makeKpiCard(idPrefix, 'freed', origin.x + (KPI_WIDTH + KPI_GAP) * 2, origin.y, {
      title: formatAllocation(freedLoad),
      subtitle: 'capacity freed',
      accent: '#059669',
      fill: '#ECFDF5',
      stroke: '#A7F3D0',
    }),
    makeKpiCard(idPrefix, 'targets', origin.x + (KPI_WIDTH + KPI_GAP) * 3, origin.y, {
      title: String(changedTargets.length),
      subtitle: 'work targets changed',
      accent: '#7C3AED',
      fill: '#F5F3FF',
      stroke: '#DDD6FE',
    }),
    makeLane(idPrefix, 'increases', increaseX, lanesY, PEOPLE_LANE_WIDTH, laneHeightPx, {
      title: 'Load increases',
      subtitle: 'People taking on more work in the comparison window',
      accent: '#EA580C',
      fill: '#FFF7ED',
      stroke: '#FDBA74',
    }),
    makeLane(idPrefix, 'decreases', decreaseX, lanesY, PEOPLE_LANE_WIDTH, laneHeightPx, {
      title: 'Capacity freed',
      subtitle: 'People with less committed load in the comparison window',
      accent: '#059669',
      fill: '#ECFDF5',
      stroke: '#A7F3D0',
    }),
    makeLane(idPrefix, 'targets', targetX, lanesY, TARGET_LANE_WIDTH, laneHeightPx, {
      title: 'Target changes',
      subtitle: 'Work contexts whose demand or staffing changed',
      accent: '#4F46E5',
      fill: '#EEF2FF',
      stroke: '#C7D2FE',
    }),
    ...visibleIncreases.map((diff, index) =>
      makePersonDiffCard(
        idPrefix,
        diff,
        increaseX + LANE_PADDING,
        laneCardY(lanesY, index, PERSON_CARD_HEIGHT, PERSON_CARD_GAP),
        'increase',
      ),
    ),
    ...visibleDecreases.map((diff, index) =>
      makePersonDiffCard(
        idPrefix,
        diff,
        decreaseX + LANE_PADDING,
        laneCardY(lanesY, index, PERSON_CARD_HEIGHT, PERSON_CARD_GAP),
        'decrease',
      ),
    ),
    ...visibleTargets.map((diff, index) =>
      makeTargetDiffCard(
        idPrefix,
        diff,
        targetX + LANE_PADDING,
        laneCardY(lanesY, index, TARGET_CARD_HEIGHT, TARGET_CARD_GAP),
      ),
    ),
  ];

  if (visibleIncreases.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'increases-empty', increaseX, lanesY, PEOPLE_LANE_WIDTH, 'No load increases.'));
  }
  if (visibleDecreases.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'decreases-empty', decreaseX, lanesY, PEOPLE_LANE_WIDTH, 'No capacity freed.'));
  }
  if (visibleTargets.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'targets-empty', targetX, lanesY, TARGET_LANE_WIDTH, 'No target demand changes.'));
  }

  return {
    nodes,
    edges: [],
    changedPeopleCount: personDiffs.length,
    changedTargetCount: changedTargets.length,
    hiddenIncreaseCount: Math.max(0, increases.length - visibleIncreases.length),
    hiddenDecreaseCount: Math.max(0, decreases.length - visibleDecreases.length),
    hiddenTargetCount: Math.max(0, changedTargets.length - visibleTargets.length),
  };
}

function buildPersonDiffs(
  beforePeople: readonly OrgPersonAllocationLoad[],
  afterPeople: readonly OrgPersonAllocationLoad[],
): PersonDiff[] {
  const beforeById = new Map(beforePeople.map((person) => [person.personExternalId, person]));
  const afterById = new Map(afterPeople.map((person) => [person.personExternalId, person]));
  const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
  return Array.from(ids)
    .map((id) => {
      const before = beforeById.get(id);
      const after = afterById.get(id);
      const beforeLoad = before?.totalAllocation ?? 0;
      const afterLoad = after?.totalAllocation ?? 0;
      const workSummary = summarizeWorkDelta(before, after);
      return {
        personExternalId: id,
        personLabel: after?.personLabel ?? before?.personLabel ?? id,
        beforeLoad,
        afterLoad,
        delta: afterLoad - beforeLoad,
        beforeStatus: before?.status ?? null,
        afterStatus: after?.status ?? null,
        beforeAssignments: before?.assignmentCount ?? 0,
        afterAssignments: after?.assignmentCount ?? 0,
        hasUnknownBefore: before?.hasUnspecifiedAllocation ?? false,
        hasUnknownAfter: after?.hasUnspecifiedAllocation ?? false,
        workSummary,
      };
    })
    .filter((diff) => Math.abs(diff.delta) > EPSILON ||
      diff.beforeAssignments !== diff.afterAssignments ||
      diff.hasUnknownBefore !== diff.hasUnknownAfter ||
      diff.beforeStatus !== diff.afterStatus);
}

function buildTargetDiffs(
  beforeTargets: readonly OrgAllocationTargetRoster[],
  afterTargets: readonly OrgAllocationTargetRoster[],
): TargetDiff[] {
  const beforeById = new Map(beforeTargets.map((target) => [target.targetExternalId, target]));
  const afterById = new Map(afterTargets.map((target) => [target.targetExternalId, target]));
  const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
  return Array.from(ids).map((id) => {
    const before = beforeById.get(id);
    const after = afterById.get(id);
    return {
      targetExternalId: id,
      targetLabel: after?.targetLabel ?? before?.targetLabel ?? id,
      targetKind: after?.targetKind ?? before?.targetKind ?? 'project',
      beforeLoad: before?.totalAllocation ?? 0,
      afterLoad: after?.totalAllocation ?? 0,
      delta: (after?.totalAllocation ?? 0) - (before?.totalAllocation ?? 0),
      beforePeople: before?.peopleCount ?? 0,
      afterPeople: after?.peopleCount ?? 0,
      hasUnknownBefore: before?.hasUnspecifiedAllocation ?? false,
      hasUnknownAfter: after?.hasUnspecifiedAllocation ?? false,
    };
  });
}

function makeKpiCard(
  prefix: string,
  key: string,
  x: number,
  y: number,
  input: {
    readonly title: string;
    readonly subtitle: string;
    readonly accent: string;
    readonly fill: string;
    readonly stroke: string;
  },
): Node {
  return {
    id: diagramId(prefix, 'kpi', key),
    type: 'org-shape',
    category: 'orgchart',
    x,
    y,
    width: KPI_WIDTH,
    height: KPI_HEIGHT,
    w: KPI_WIDTH,
    h: KPI_HEIGHT,
    zIndex: 5,
    attrs: {
      variant: 'role',
      title: input.title,
      subtitle: input.subtitle,
      fillColor: input.fill,
      headerColor: input.accent,
      strokeColor: input.stroke,
      strokeWidth: 1,
      textColor: '#0F172A',
      subtitleColor: '#334155',
      atlasDiff: { kind: 'kpi', key },
    },
  } as Node;
}

function makeLane(
  prefix: string,
  key: string,
  x: number,
  y: number,
  width: number,
  height: number,
  input: {
    readonly title: string;
    readonly subtitle: string;
    readonly accent: string;
    readonly fill: string;
    readonly stroke: string;
  },
): Node {
  return {
    id: diagramId(prefix, 'lane', key),
    type: 'org-shape',
    category: 'orgchart',
    x,
    y,
    width,
    height,
    w: width,
    h: height,
    zIndex: 0,
    attrs: {
      variant: 'department',
      title: input.title,
      subtitle: input.subtitle,
      fillColor: input.fill,
      headerColor: input.accent,
      strokeColor: input.stroke,
      strokeWidth: 1,
      textColor: '#0F172A',
      subtitleColor: '#475569',
      atlasDiff: { kind: 'lane', key },
    },
  } as Node;
}

function makePersonDiffCard(
  prefix: string,
  diff: PersonDiff,
  x: number,
  y: number,
  lane: 'increase' | 'decrease',
): Node {
  const palette = lane === 'increase'
    ? { accent: '#EA580C', border: '#FDBA74', fill: '#FFFFFF' }
    : { accent: '#059669', border: '#A7F3D0', fill: '#FFFFFF' };
  return {
    id: diagramId(prefix, 'person', `${lane}:${diff.personExternalId}`),
    type: 'org_person_text',
    category: 'orgchart',
    name: diff.personLabel,
    x,
    y,
    width: PERSON_CARD_WIDTH,
    height: PERSON_CARD_HEIGHT,
    w: PERSON_CARD_WIDTH,
    h: PERSON_CARD_HEIGHT,
    zIndex: 10,
    attrs: {
      personId: diff.personExternalId,
      name: diff.personLabel,
      role: `${formatAllocation(diff.beforeLoad)} -> ${formatAllocation(diff.afterLoad)} (${formatSignedAllocation(diff.delta)})`,
      fillColor: palette.fill,
      accentColor: palette.accent,
      borderColor: palette.border,
      strokeColor: palette.border,
      strokeWidth: 2,
      textColor: '#04263D',
      subtitleColor: palette.accent,
      customFields: [
        {
          id: 'atlasDiffStatus',
          name: 'Status',
          type: 'text',
          value: `${statusLabel(diff.beforeStatus)} -> ${statusLabel(diff.afterStatus)}`,
          showOnCard: true,
        },
        {
          id: 'atlasDiffCommitments',
          name: 'Links',
          type: 'text',
          value: `${diff.beforeAssignments} -> ${diff.afterAssignments}`,
          showOnCard: true,
        },
        {
          id: 'atlasDiffWork',
          name: 'Work',
          type: 'text',
          value: diff.workSummary,
          showOnCard: true,
        },
      ],
      visibleFields: {
        custom_atlasDiffStatus: true,
        custom_atlasDiffCommitments: true,
        custom_atlasDiffWork: true,
      },
      atlasDiff: {
        kind: 'person_diff',
        lane,
        personExternalId: diff.personExternalId,
        beforeLoad: diff.beforeLoad,
        afterLoad: diff.afterLoad,
        delta: diff.delta,
        beforeStatus: diff.beforeStatus,
        afterStatus: diff.afterStatus,
      },
    },
  } as Node;
}

function makeTargetDiffCard(
  prefix: string,
  diff: TargetDiff,
  x: number,
  y: number,
): Node {
  const palette = diff.delta >= 0 ? targetPalette('increase') : targetPalette('decrease');
  return {
    id: diagramId(prefix, 'target', diff.targetExternalId),
    type: 'org_project',
    category: 'orgchart',
    name: diff.targetLabel,
    x,
    y,
    width: TARGET_CARD_WIDTH,
    height: TARGET_CARD_HEIGHT,
    w: TARGET_CARD_WIDTH,
    h: TARGET_CARD_HEIGHT,
    zIndex: 10,
    attrs: {
      name: diff.targetLabel,
      projectType: diff.targetKind,
      status: `${humanizeIdentifier(diff.targetKind)} - ${formatAllocation(diff.beforeLoad)} -> ${formatAllocation(diff.afterLoad)} (${formatSignedAllocation(diff.delta)})`,
      memberCount: diff.afterPeople,
      completion: Math.round(diff.afterLoad * 100),
      customFields: [],
      accentColor: palette.accentColor,
      fillColor: palette.fillColor,
      textColor: palette.textColor,
      borderColor: palette.borderColor,
      atlasDiff: {
        kind: 'target_diff',
        targetExternalId: diff.targetExternalId,
        targetKind: diff.targetKind,
        beforeLoad: diff.beforeLoad,
        afterLoad: diff.afterLoad,
        delta: diff.delta,
        beforePeople: diff.beforePeople,
        afterPeople: diff.afterPeople,
      },
    },
  } as Node;
}

function makeLaneNote(prefix: string, key: string, laneX: number, laneY: number, laneWidth: number, text: string): Node {
  return {
    id: diagramId(prefix, 'note', key),
    type: 'text',
    x: laneX + LANE_PADDING,
    y: laneY + LANE_HEADER_RESERVE,
    width: laneWidth - LANE_PADDING * 2,
    height: 64,
    w: laneWidth - LANE_PADDING * 2,
    h: 64,
    zIndex: 10,
    fillColor: 'rgba(255,255,255,0.72)',
    strokeColor: '#CBD5E1',
    strokeWidth: 1,
    text,
    attrs: {
      text,
      fontSize: 14,
      textAlign: 'center',
      verticalAlign: 'middle',
      textColor: '#64748B',
      atlasDiff: { kind: 'empty_note', key },
    },
  };
}

function laneHeight(count: number, cardHeight: number, gap: number): number {
  if (count <= 0) return LANE_HEADER_RESERVE + LANE_PADDING + 92;
  return LANE_HEADER_RESERVE + LANE_PADDING +
    count * cardHeight +
    Math.max(0, count - 1) * gap +
    LANE_PADDING;
}

function laneCardY(laneY: number, index: number, cardHeight: number, gap: number): number {
  return laneY + LANE_HEADER_RESERVE + index * (cardHeight + gap);
}

function summarizeWorkDelta(
  before: OrgPersonAllocationLoad | undefined,
  after: OrgPersonAllocationLoad | undefined,
): string {
  const beforeTargets = new Set((before?.assignments ?? []).map((assignment) => assignment.targetLabel));
  const afterTargets = new Set((after?.assignments ?? []).map((assignment) => assignment.targetLabel));
  const added = [...afterTargets].filter((target) => !beforeTargets.has(target));
  const removed = [...beforeTargets].filter((target) => !afterTargets.has(target));
  if (added.length > 0) return `Added ${added.slice(0, 2).join(', ')}`;
  if (removed.length > 0) return `Freed from ${removed.slice(0, 2).join(', ')}`;
  const steady = [...afterTargets].slice(0, 2);
  return steady.length > 0 ? steady.join(', ') : 'No named target';
}

function sumPositiveDelta(diffs: readonly PersonDiff[]): number {
  return diffs.reduce((sum, diff) => sum + Math.max(0, diff.delta), 0);
}

function sumNegativeDelta(diffs: readonly PersonDiff[]): number {
  return diffs.reduce((sum, diff) => sum + Math.min(0, diff.delta), 0);
}

function targetPalette(direction: 'increase' | 'decrease'): {
  readonly accentColor: string;
  readonly fillColor: string;
  readonly borderColor: string;
  readonly textColor: string;
} {
  if (direction === 'decrease') {
    return { accentColor: '#059669', fillColor: '#ECFDF5', borderColor: '#A7F3D0', textColor: '#064E3B' };
  }
  return { accentColor: '#EA580C', fillColor: '#FFF7ED', borderColor: '#FDBA74', textColor: '#7C2D12' };
}

function statusLabel(status: AllocationLoadStatus | null): string {
  if (!status) return 'No load';
  return humanizeIdentifier(status);
}

function boundedLimit(value: number | undefined, fallback: number): number {
  if (value == null || !Number.isFinite(value)) return fallback;
  return Math.max(1, Math.floor(value));
}

function snapPoint(point: { readonly x: number; readonly y: number }): { readonly x: number; readonly y: number } {
  return {
    x: Math.round(point.x / GRID) * GRID,
    y: Math.round(point.y / GRID) * GRID,
  };
}

function diagramId(prefix: string, kind: string, value: string): string {
  return `${prefix}-${kind}-${stableToken(value)}`;
}

function stableToken(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36) || 'item';
  return `${slug}-${hashString(value)}`;
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 2_147_483_647;
  }
  return Math.abs(hash).toString(36);
}

function formatAllocation(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatSignedAllocation(value: number): string {
  const rounded = Math.round(value * 100);
  if (rounded > 0) return `+${rounded}%`;
  return `${rounded}%`;
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
